import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Types ── */
interface HashType {
  id?: number;
  name: string;
  origin_country?: string;
  country_flag?: string;
  image_url?: string;
  texture?: string;
  potency?: string;
  color?: string;
  production_method?: string;
  history?: string;
  description?: string;
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

export default function HashTab(_props: CountryProps) {
  const [hashTypes, setHashTypes] = useState<HashType[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch(getApiUrl('/api/hash-types'));
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setHashTypes(Array.isArray(data) ? data : data.data ?? []);
      } catch {
        setHashTypes([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const hashKey = (h: HashType, i: number) => h.id ? String(h.id) : `${h.name}-${i}`;

  const handleImageError = (hashName: string) => {
    setFailedImages(prev => new Set([...prev, hashName]));
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-56" />)}
      </div>
    );
  }

  if (hashTypes.length === 0) {
    return (
      <div className="text-center py-16 text-white/40">
        <p>No hash types available.</p>
      </div>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
    >
      {hashTypes.map((h, i) => {
        const key = hashKey(h, i);
        const isExpanded = expandedId === key;

        return (
          <motion.div key={key} variants={staggerItem} layout>
            <div
              onClick={() => setExpandedId(isExpanded ? null : key)}
              className={`cursor-pointer rounded-2xl border p-4 transition-colors ${
                isExpanded
                  ? 'border-[#d4a574]/40 bg-white/[0.04]'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20'
              }`}
            >
              {!failedImages.has(h.name) && h.image_url ? (
                <img
                  src={h.image_url}
                  alt={h.name}
                  className="w-full h-36 object-cover rounded-xl mb-3"
                  onError={() => handleImageError(h.name)}
                />
              ) : (
                <div className="w-full h-36 rounded-xl mb-3 bg-gradient-to-br from-[#d4a574]/20 to-amber-900/20 border border-amber-500/20 flex items-center justify-center">
                  <span className="text-3xl">⬛</span>
                </div>
              )}
              <div className="space-y-2">
                <h3 className="text-white font-semibold text-sm">{h.name}</h3>
                {h.origin_country && (
                  <p className="text-white/50 text-xs">
                    {h.country_flag ?? ''} {h.origin_country}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 text-xs">
                  {h.texture && (
                    <span className="px-2 py-0.5 rounded-full bg-[#d4a574]/10 text-[#d4a574] border border-[#d4a574]/20">
                      {h.texture}
                    </span>
                  )}
                  {h.potency && (
                    <span className="px-2 py-0.5 rounded-full bg-[#4ade80]/10 text-[#4ade80] border border-[#4ade80]/20">
                      {h.potency}
                    </span>
                  )}
                </div>
              </div>

              {/* Expanded Detail */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                      {h.description && (
                        <p className="text-white/60 text-xs leading-relaxed">{h.description}</p>
                      )}
                      {h.production_method && (
                        <div>
                          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Production Method</p>
                          <p className="text-white/70 text-xs">{h.production_method}</p>
                        </div>
                      )}
                      {h.history && (
                        <div>
                          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">History</p>
                          <p className="text-white/70 text-xs">{h.history}</p>
                        </div>
                      )}
                      {h.color && (
                        <p className="text-white/40 text-xs">
                          Color: <span className="text-white/60">{h.color}</span>
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
